import { useState } from 'react';
import { generatePalette } from '../utils/palette';

export function usePaletteGenerator(initialOptions = {}) {
  const {
    initialCount = 9,
    initialBrand = 'All',
    initialFilter = 'Random'
  } = initialOptions;

  const [palette, setPalette] = useState([]);
  const [count, setCount] = useState(initialCount);
  const [filter, setFilter] = useState(initialFilter);
  const [similarSubfilter, setSimilarSubfilter] = useState('All Hues');
  const [locks, setLocks] = useState({});

  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);

  const HISTORY_LIMIT = 7;

  const clonePalette = (items = []) => items.map((item) => ({ ...item }));
  const cloneLocks = (lockState = {}) => Object.keys(lockState).reduce((acc, key) => {
    acc[key] = lockState[key];
    return acc;
  }, {});

  const createSnapshot = () => ({
    palette: clonePalette(palette),
    locks: cloneLocks(locks),
    filter,
    similarSubfilter,
    count
  });

  const restoreSnapshot = (snapshot) => {
    if (!snapshot) {
      return;
    }

    setFilter(snapshot.filter ?? filter);
    setSimilarSubfilter(snapshot.similarSubfilter ?? similarSubfilter);
    setCount(snapshot.count ?? count);
    setLocks(cloneLocks(snapshot.locks));
    setPalette(clonePalette(snapshot.palette));
  };

  // Generate palette when parameters change
  const regeneratePalette = (options = {}) => {
    const {
      newCount = count,
      newFilter = filter,
      newSimilarSubfilter = similarSubfilter,
      brand = initialBrand,
      ohuhuSet = null,
      arrtxSet = null,
      sfaihSet = null,
      collectionBrand = null,
      caliartSet = null,
      bianyoSet = null,
      hotuSet = null,
      calmOverChaosSet = null,
      hangfanSet = null,
      sanjokiSet = null,
      prismSet = null,
      royalLangnickelSet = null,
      copicSketchSet = null,
      copicClassicSet = null,
      copicCiaoSet = null,
      keepLocks = true,
      addToHistory = true
    } = options;

    const currentLocks = keepLocks ? locks : {};
    let attempts = 0;
    let next;
    do {
      next = generatePalette({
        count: newCount,
        brand,
        filter: newFilter,
        similarSubfilter: newSimilarSubfilter,
        ohuhuSet,
        arrtxSet,
        sfaihSet,
        collectionBrand,
        caliartSet,
        bianyoSet,
        hotuSet,
        calmOverChaosSet,
        hangfanSet,
        sanjokiSet,
        prismSet,
        royalLangnickelSet,
        copicSketchSet,
        copicClassicSet,
        copicCiaoSet,
        locked: currentLocks,
        current: palette
      });
      attempts++;
    } while (
      attempts < 3 &&
      next.some((m) => !m || typeof m.hex !== 'string' || !m.hex)
    );

    if (addToHistory && palette.length > 0) {
      const snapshot = createSnapshot();
      setHistory(prev => {
        const updated = [...prev, snapshot];
        return updated.slice(-HISTORY_LIMIT);
      });
      setFuture([]);
    }

    if (newCount !== count) {
      setCount(newCount);
    }

    if (newFilter !== filter) {
      setFilter(newFilter);
    }

    if (newSimilarSubfilter !== similarSubfilter) {
      setSimilarSubfilter(newSimilarSubfilter);
    }

    setPalette(next);
  };

  // Voltar para paleta anterior na categoria atual
  const goToPreviousPalette = () => {
    if (history.length === 0) {
      return;
    }

    const previousEntry = history[history.length - 1];
    const updatedHistory = history.slice(0, -1);
    const currentSnapshot = createSnapshot();

    setHistory(updatedHistory);
    setFuture(prevFuture => {
      const updatedFuture = [...prevFuture, currentSnapshot];
      return updatedFuture.slice(-HISTORY_LIMIT);
    });

    restoreSnapshot(previousEntry);
  };

  // Avançar para paleta seguinte na categoria atual
  const goToNextPalette = () => {
    if (future.length === 0) {
      return;
    }

    const nextEntry = future[future.length - 1];
    const updatedFuture = future.slice(0, -1);
    const currentSnapshot = createSnapshot();

    setFuture(updatedFuture);
    setHistory(prevHistory => {
      const updatedHistory = [...prevHistory, currentSnapshot];
      return updatedHistory.slice(-HISTORY_LIMIT);
    });

    restoreSnapshot(nextEntry);
  };

  // Verificar se há história disponível
  const hasPreviousPalette = () => history.length > 0;

  // Verificar se há histórico para frente disponível
  const hasNextPalette = () => future.length > 0;

  // Toggle lock for a specific color
  const toggleLock = (index) => {
    setLocks(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  // Set count with bounds checking and lock adjustment
  const setCountClamped = (newCount) => {
    const clamped = Math.min(20, Math.max(3, newCount));
    
    // Adjust locks for new length
    const newLocks = {};
    for (let i = 0; i < clamped; i++) {
      newLocks[i] = !!locks[i];
    }
    
    setLocks(newLocks);
    setCount(clamped);
  };

  // Set filter and regenerate
  const setFilterAndRegenerate = (newFilter, brand = initialBrand, extraOptions = {}) => {
    setFilter(newFilter);
    if (newFilter !== 'Similar') {
      setSimilarSubfilter('All Hues'); // Reset subfilter when leaving Similar
    }

    regeneratePalette({ newFilter, brand, ...extraOptions });
  };

  // Set similar subfilter and regenerate
  const setSimilarSubfilterAndRegenerate = (newSubfilter, brand = initialBrand, extraOptions = {}) => {
    setSimilarSubfilter(newSubfilter);

    regeneratePalette({ newSimilarSubfilter: newSubfilter, brand, ...extraOptions });
  };

  // Replace a specific color in the current palette
  const replaceColorAt = (index, newColor) => {
    if (typeof index !== 'number' || index < 0 || index >= count || !newColor) {
      return;
    }

    if (palette.length > 0) {
      const snapshot = createSnapshot();
      setHistory(prev => {
        const updated = [...prev, snapshot];
        return updated.slice(-HISTORY_LIMIT);
      });
      setFuture([]);
    }

    setPalette(prev => {
      const next = [...prev];
      next[index] = { ...newColor };
      return next;
    });
  };

  // Clear all locks
  const clearLocks = () => {
    setLocks({});
  };

  // Reset to initial state
  const reset = (brand = initialBrand) => {
    setCount(initialCount);
    setFilter(initialFilter);
    setSimilarSubfilter('All Hues');
    setLocks({});
    setPalette([]);
    setHistory([]);
    setFuture([]);
  };

  // Load a palette directly (for loading from saved palettes or URL)
  const loadPalette = (newPalette) => {
    if (!newPalette || !Array.isArray(newPalette) || newPalette.length === 0) {
      return;
    }

    // Clamp to valid range — same bounds as setCountClamped
    const clamped = newPalette.slice(0, 20);

    // Add current palette to history before loading new one
    if (palette.length > 0) {
      const snapshot = createSnapshot();
      setHistory(prev => {
        const updated = [...prev, snapshot];
        return updated.slice(-HISTORY_LIMIT);
      });
      setFuture([]);
    }

    setPalette(clonePalette(clamped));
    setCount(clamped.length);
    setLocks({}); // Clear all locks when loading a new palette
  };

  return {
    // State
    palette,
    count,
    filter,
    similarSubfilter,
    locks,

    // Actions
    regeneratePalette,
    toggleLock,
    setCountClamped,
    setFilterAndRegenerate,
    setSimilarSubfilterAndRegenerate,
    clearLocks,
    reset,
    replaceColorAt,
    loadPalette,

    // História
    goToPreviousPalette,
    hasPreviousPalette,
    goToNextPalette,
    hasNextPalette,

    // Utilities
    isLocked: (index) => !!locks[index],
    lockedCount: Object.values(locks).filter(Boolean).length,
    isSimilarMode: filter === 'Similar'
  };
}
